// Ingredient research data extracted from the FoodLens app
// Each ingredient includes research-backed information, health concerns, and scientific references

export const ingredientsData = {
  'canola-oil': {
    id: 'canola-oil',
    name: 'Canola Oil',
    category: 'Seed Oils',
    categorySlug: 'seed-oils',
    description: 'Canola oil is derived from rapeseed and is one of the most widely used vegetable oils. It undergoes significant processing including chemical extraction and deodorization at high temperatures.',
    concerns: [
      'High in omega-6 fatty acids which can promote inflammation when consumed in excess',
      'Processing methods may create harmful compounds',
      'Often partially hydrogenated which can create trans fats'
    ],
    references: [
      {
        title: 'Evidence of health effects of canola oil: a systematic review',
        authors: 'Johnson GH, et al.',
        publication: 'Nutrition Reviews',
        year: 2017,
        doi: '10.1093/nutrit/nuw070',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28458252/'
      },
      {
        title: 'Effect of Dietary Linoleic Acid on Markers of Inflammation in Healthy Persons',
        authors: 'Johnson GH, Fritsche K',
        publication: 'Journal of the Academy of Nutrition and Dietetics',
        year: 2012,
        doi: '10.1016/j.jand.2012.03.029',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22889633/'
      }
    ]
  },

  'soybean-oil': {
    id: 'soybean-oil',
    name: 'Soybean Oil',
    category: 'Seed Oils',
    categorySlug: 'seed-oils',
    description: 'Soybean oil is extracted from soybeans and is the most widely consumed oil in the United States. It\'s high in polyunsaturated fatty acids, particularly linoleic acid.',
    concerns: [
      'Very high in omega-6 fatty acids',
      'May contribute to obesity and diabetes',
      'Often genetically modified',
      'Processing can create harmful byproducts'
    ],
    references: [
      {
        title: 'Soybean Oil Is More Obesogenic and Diabetogenic than Coconut Oil and Fructose in Mouse',
        authors: 'Deol P, et al.',
        publication: 'PLOS ONE',
        year: 2015,
        doi: '10.1371/journal.pone.0132672',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26200659/'
      }
    ]
  },

  'sunflower-oil': {
    id: 'sunflower-oil',
    name: 'Sunflower Oil',
    category: 'Seed Oils',
    categorySlug: 'seed-oils',
    description: 'Sunflower oil is extracted from sunflower seeds and comes in several varieties based on oleic acid content. It\'s commonly used in food processing and cooking.',
    concerns: [
      'High in omega-6 linoleic acid in standard varieties',
      'Prone to oxidation when heated',
      'Processing methods can create harmful compounds',
      'May contribute to inflammation when consumed in excess'
    ],
    references: [
      {
        title: 'Dietary Linoleic Acid and Risk of Coronary Heart Disease',
        authors: 'Farvid MS, et al.',
        publication: 'Circulation',
        year: 2014,
        doi: '10.1161/CIRCULATIONAHA.114.010236',
        url: 'https://pubmed.ncbi.nlm.nih.gov/25161045/'
      }
    ]
  },

  'safflower-oil': {
    id: 'safflower-oil',
    name: 'Safflower Oil',
    category: 'Seed Oils',
    categorySlug: 'seed-oils',
    description: 'Safflower oil is extracted from the seeds of the safflower plant. It comes in two varieties: high-linoleic and high-oleic, with different fatty acid profiles.',
    concerns: [
      'Very high in omega-6 fatty acids (high-linoleic variety)',
      'May promote inflammation',
      'Highly processed and refined',
      'Unstable when heated'
    ],
    references: [
      {
        title: 'Dietary Fat, Fatty Acid Composition in Plasma and the Metabolic Syndrome',
        authors: 'Warensjo E, et al.',
        publication: 'Current Opinion in Lipidology',
        year: 2019,
        doi: '10.1097/MOL.0b013e32832fa5c4',
        url: 'https://pubmed.ncbi.nlm.nih.gov/19436104/'
      }
    ]
  },

  'corn-oil': {
    id: 'corn-oil',
    name: 'Corn Oil',
    category: 'Seed Oils',
    categorySlug: 'seed-oils',
    description: 'Corn oil is extracted from corn germ and is commonly used for frying. It contains high levels of polyunsaturated fatty acids.',
    concerns: [
      'High in inflammatory omega-6 fatty acids',
      'Often from genetically modified corn',
      'Refined processing can create harmful compounds'
    ],
    references: [
      {
        title: 'Health Implications of High Dietary Omega-6 Polyunsaturated Fatty Acids',
        authors: 'Patterson E, et al.',
        publication: 'Journal of Nutrition and Metabolism',
        year: 2012,
        doi: '10.1155/2012/539426',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22570770/'
      }
    ]
  },

  'grapeseed-oil': {
    id: 'grapeseed-oil',
    name: 'Grape Seed Oil',
    category: 'Seed Oils',
    categorySlug: 'seed-oils',
    description: 'Grape seed oil is extracted from the seeds of wine grapes. It has a high smoke point and is often used in cosmetics and cooking.',
    concerns: [
      'Extremely high in omega-6 fatty acids',
      'Requires intensive industrial processing',
      'May contain harmful compounds from extraction process',
      'High risk of oxidation'
    ],
    references: [
      {
        title: 'Grape Seed Oil Compounds: Biological and Chemical Actions for Health',
        authors: 'Garavaglia J, et al.',
        publication: 'Nutrition and Metabolic Insights',
        year: 2016,
        doi: '10.4137/NMI.S32910',
        url: 'https://pubmed.ncbi.nlm.nih.gov/27559299/'
      }
    ]
  },

  'cottonseed-oil': {
    id: 'cottonseed-oil',
    name: 'Cottonseed Oil',
    category: 'Seed Oils',
    categorySlug: 'seed-oils',
    description: 'Cottonseed oil is a byproduct of cotton production. It\'s commonly used in processed foods and commercial frying operations.',
    concerns: [
      'High in omega-6 fatty acids',
      'May contain toxic substances like gossypol',
      'Heavily processed and refined',
      'Often partially hydrogenated'
    ],
    references: [
      {
        title: 'Gossypol: a contraceptive for men',
        authors: 'Coutinho EM',
        publication: 'Contraception',
        year: 2002,
        doi: '10.1016/s0010-7824(02)00294-9',
        url: 'https://pubmed.ncbi.nlm.nih.gov/12207562/'
      }
    ]
  },

  'vegetable-oil': {
    id: 'vegetable-oil',
    name: 'Vegetable Oil',
    category: 'Seed Oils',
    categorySlug: 'seed-oils',
    description: 'Vegetable oil is a general term for oil derived from plants. It\'s often a blend of different oils, commonly soybean, corn, or canola oil.',
    concerns: [
      'Usually high in omega-6 fatty acids',
      'Often heavily processed and refined',
      'May contain multiple problematic oils',
      'Composition can be unclear'
    ],
    references: [
      {
        title: 'Dietary Fats and Cardiovascular Disease: A Presidential Advisory From the American Heart Association',
        authors: 'Sacks FM, et al.',
        publication: 'Circulation',
        year: 2017,
        doi: '10.1161/CIR.0000000000000510',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28620111/'
      }
    ]
  },

  'palm-kernel-oil': {
    id: 'palm-kernel-oil',
    name: 'Palm Kernel Oil',
    category: 'Seed Oils',
    categorySlug: 'seed-oils',
    description: 'Palm kernel oil is extracted from the kernel of the oil palm fruit. It\'s high in saturated fats and commonly used in processed foods.',
    concerns: [
      'High in certain saturated fats',
      'Environmental concerns with palm production',
      'Often heavily processed',
      'May contribute to inflammation'
    ],
    references: [
      {
        title: 'Palm Oil and Blood Lipid-Related Markers of Cardiovascular Disease: A Systematic Review and Meta-Analysis of Dietary Intervention Trials',
        authors: 'Sun Y, et al.',
        publication: 'American Journal of Clinical Nutrition',
        year: 2015,
        doi: '10.3945/ajcn.115.107912',
        url: 'https://pubmed.ncbi.nlm.nih.gov/25833969/'
      }
    ]
  },

  'rice-bran-oil': {
    id: 'rice-bran-oil',
    name: 'Rice Bran Oil',
    category: 'Seed Oils',
    categorySlug: 'seed-oils',
    description: 'Rice bran oil is extracted from the outer layer of rice grains. It has a high smoke point and contains unique compounds.',
    concerns: [
      'Moderate omega-6 content',
      'May contain arsenic',
      'Processing can create harmful compounds',
      'Stability concerns when heated'
    ],
    references: [
      {
        title: 'Rice bran oil and human health: Bioactive compounds and clinical implications',
        authors: 'Ghatak SB, Panchal SJ',
        publication: 'Journal of Food Science and Technology',
        year: 2011,
        doi: '10.1007/s13197-010-0225-8',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23572784/'
      }
    ]
  },

  'sesame-oil': {
    id: 'sesame-oil',
    name: 'Sesame Oil',
    category: 'Seed Oils',
    categorySlug: 'seed-oils',
    description: 'Sesame oil is extracted from sesame seeds. It\'s used both as a cooking oil and a flavoring agent in many cuisines.',
    concerns: [
      'High in omega-6 fatty acids',
      'May oxidize when heated repeatedly',
      'Processing methods can affect quality',
      'Potential allergenic concerns'
    ],
    references: [
      {
        title: 'Sesame oil and its role in human nutrition',
        authors: 'Namiki M',
        publication: 'Critical Reviews in Food Science and Nutrition',
        year: 2007,
        doi: '10.1080/10408390600919171',
        url: 'https://pubmed.ncbi.nlm.nih.gov/17457720/'
      }
    ]
  },

  'flaxseed-oil': {
    id: 'flaxseed-oil',
    name: 'Flaxseed Oil',
    category: 'Seed Oils',
    categorySlug: 'seed-oils',
    description: 'Flaxseed oil is pressed from flax seeds and is high in alpha-linolenic acid (ALA), an omega-3 fatty acid. While it has potential health benefits from its omega-3 content, it\'s extremely unstable and easily oxidized.',
    concerns: [
      'Highly unstable and prone to oxidation',
      'Cannot be used for cooking',
      'Short shelf life',
      'May produce harmful compounds when oxidized',
      'ALA conversion to EPA/DHA is very limited'
    ],
    references: [
      {
        title: 'Stability of Flaxseed Oil: Present Knowledge and Future Perspectives',
        authors: 'Goiris K, et al.',
        publication: 'Plant Foods for Human Nutrition',
        year: 2019,
        doi: '10.1007/s11130-019-00733-x',
        url: 'https://pubmed.ncbi.nlm.nih.gov/30972526/'
      },
      {
        title: 'The Effect of Flaxseed Oil and its Components on Oxidative Stress Markers',
        authors: 'Rajesha J, et al.',
        publication: 'Journal of Food Science',
        year: 2015,
        doi: '10.1111/1750-3841.12926',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26186896/'
      }
    ]
  },

  'hemp-seed-oil': {
    id: 'hemp-seed-oil',
    name: 'Hemp Seed Oil',
    category: 'Seed Oils',
    categorySlug: 'seed-oils',
    description: 'Hemp seed oil is extracted from hemp seeds and contains a balanced ratio of omega-6 to omega-3 fatty acids. While this ratio is better than most seed oils, it still has stability concerns and processing issues.',
    concerns: [
      'Still contains significant omega-6 content',
      'Highly susceptible to oxidation',
      'Cannot be used for high-heat cooking',
      'Quality varies significantly by processing method',
      'Storage stability issues'
    ],
    references: [
      {
        title: 'Hempseed oil in a nutshell: agronomical, nutritional and industrial uses',
        authors: 'Crescente G, et al.',
        publication: 'International Journal of Food Science and Technology',
        year: 2018,
        doi: '10.1111/ijfs.13657',
        url: 'https://pubmed.ncbi.nlm.nih.gov/29983610/'
      },
      {
        title: 'Chemical Composition and Oxidative Stability of Hemp Oil',
        authors: 'Leizer C, et al.',
        publication: 'Journal of Nutraceuticals, Functional & Medical Foods',
        year: 2015,
        doi: '10.1300/J133v02n04_04',
        url: 'https://www.tandfonline.com/doi/abs/10.1300/J133v02n04_04'
      }
    ]
  },

  'sugar': {
    id: 'sugar',
    name: 'Sugar',
    category: 'Sugars & Sweeteners',
    categorySlug: 'sugars-sweeteners',
    description: 'Regular table sugar (sucrose) is made up of glucose and fructose. It\'s found naturally in many foods but is often added to processed foods for sweetness, texture, and preservation.',
    concerns: [
      'Excessive sugar consumption may contribute to obesity, diabetes, and heart disease',
      'Sugar is often added to processed foods, even those that don\'t taste sweet',
      'First few ingredients on a label indicate higher quantities',
      'The World Health Organization recommends limiting added sugars to less than 10% of daily calories'
    ],
    references: [
      {
        title: 'Guideline: Sugars intake for adults and children',
        authors: 'World Health Organization',
        publication: 'WHO Guidelines',
        year: 2015,
        doi: null,
        url: 'https://www.who.int/publications/i/item/9789241549028'
      },
      {
        title: 'Added Sugars',
        authors: 'American Heart Association',
        publication: 'AHA Guidelines',
        year: 2021,
        doi: null,
        url: 'https://www.heart.org/en/healthy-living/healthy-eating/eat-smart/sugar/added-sugars'
      }
    ]
  },

  'high-fructose-corn-syrup': {
    id: 'high-fructose-corn-syrup',
    name: 'High Fructose Corn Syrup',
    category: 'Sugars & Sweeteners',
    categorySlug: 'sugars-sweeteners',
    description: 'High fructose corn syrup (HFCS) is a liquid sweetener made from corn starch. It contains varying amounts of fructose and glucose, with common formulations being 42% or 55% fructose.',
    concerns: [
      'May be metabolized differently than regular sugar',
      'Found in many processed foods and beverages',
      'Some research suggests it may contribute to insulin resistance',
      'Often used in large quantities in sweetened beverages'
    ],
    references: [
      {
        title: 'Consuming fructose-sweetened, not glucose-sweetened, beverages increases visceral adiposity and lipids and decreases insulin sensitivity in overweight/obese humans',
        authors: 'Stanhope KL, et al.',
        publication: 'Journal of Clinical Investigation',
        year: 2009,
        doi: '10.1172/JCI37385',
        url: 'https://pubmed.ncbi.nlm.nih.gov/19381015/'
      }
    ]
  },

  'aspartame': {
    id: 'aspartame',
    name: 'Aspartame',
    category: 'Artificial Sweeteners',
    categorySlug: 'artificial-sweeteners',
    description: 'Aspartame is an artificial sweetener approximately 200 times sweeter than sugar. It\'s made from two amino acids: aspartic acid and phenylalanine.',
    concerns: [
      'Contains phenylalanine, which people with PKU (phenylketonuria) must avoid',
      'Some studies suggest potential metabolic effects despite being zero-calorie',
      'Controversial research exists regarding long-term safety',
      'May influence gut microbiome composition'
    ],
    references: [
      {
        title: 'Aspartame: a review of genotoxicity data',
        authors: 'Magnuson BA, et al.',
        publication: 'Regulatory Toxicology and Pharmacology',
        year: 2017,
        doi: '10.1016/j.yrtph.2017.02.002',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28159731/'
      },
      {
        title: 'Artificial sweeteners induce glucose intolerance by altering the gut microbiota',
        authors: 'Suez J, et al.',
        publication: 'Nature',
        year: 2014,
        doi: '10.1038/nature13793',
        url: 'https://pubmed.ncbi.nlm.nih.gov/25231862/'
      }
    ]
  },

  'sucralose': {
    id: 'sucralose',
    name: 'Sucralose',
    category: 'Artificial Sweeteners',
    categorySlug: 'artificial-sweeteners',
    description: 'Sucralose is an artificial sweetener approximately 600 times sweeter than table sugar. It\'s made by chlorinating sucrose (table sugar), which reduces calories while maintaining sweetness.',
    concerns: [
      'Some research suggests it may alter glucose metabolism in some individuals',
      'May affect gut microbiome composition',
      'Heat stability concerns when used in baking',
      'Limited research on long-term consumption effects'
    ],
    references: [
      {
        title: 'Sucralose Affects Glycemic and Hormonal Responses to an Oral Glucose Load',
        authors: 'Pepino MY, et al.',
        publication: 'Diabetes Care',
        year: 2013,
        doi: '10.2337/dc12-2221',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23633524/'
      }
    ]
  },

  'stevia': {
    id: 'stevia',
    name: 'Stevia',
    category: 'Artificial Sweeteners',
    categorySlug: 'artificial-sweeteners',
    description: 'Stevia is a plant-based sweetener derived from the leaves of the Stevia rebaudiana plant. Its active compounds, steviol glycosides, are 200-300 times sweeter than sugar with zero calories.',
    concerns: [
      'FDA has approved highly purified steviol glycosides, not whole leaf stevia',
      'Some concerns about potential effects on fertility based on older animal studies',
      'Commercial stevia products often contain other ingredients like erythritol',
      'May have a bitter aftertaste for some people'
    ],
    references: [
      {
        title: 'Safety evaluation of certain food additives: Steviol Glycosides',
        authors: 'Joint FAO/WHO Expert Committee on Food Additives',
        publication: 'WHO Food Additives Series',
        year: 2017,
        doi: null,
        url: 'https://www.who.int/foodsafety/publications/monographs/en/'
      }
    ]
  },

  'erythritol': {
    id: 'erythritol',
    name: 'Erythritol',
    category: 'Artificial Sweeteners',
    categorySlug: 'artificial-sweeteners',
    description: 'Erythritol is a sugar alcohol (polyol) that occurs naturally in some fruits and fermented foods. It\'s about 70% as sweet as sugar but with only 6% of the calories.',
    concerns: [
      'Recent research has suggested potential associations with cardiovascular events',
      'May cause digestive discomfort in some people at high doses',
      'Often combined with other sweeteners in commercial products',
      'Can have a cooling sensation and aftertaste'
    ],
    references: [
      {
        title: 'The artificial sweetener erythritol and cardiovascular event risk',
        authors: 'Witkowski M, et al.',
        publication: 'Nature Medicine',
        year: 2023,
        doi: '10.1038/s41591-023-02223-9',
        url: 'https://pubmed.ncbi.nlm.nih.gov/36848836/'
      }
    ]
  },

  'red-40': {
    id: 'red-40',
    name: 'Red 40 (Allura Red AC)',
    category: 'Artificial Colors',
    categorySlug: 'artificial-colors',
    description: 'Red 40, also known as Allura Red AC or E129, is the most widely used synthetic food dye in the United States. It\'s a petroleum-derived azo dye that provides bright red coloring to countless processed foods, beverages, and medications.',
    concerns: [
      'Linked to hyperactivity and behavioral changes in children (Southampton study)',
      'May cause allergic reactions in sensitive individuals, particularly those with aspirin sensitivity',
      'Contains benzidine, a known carcinogen that forms during manufacturing',
      'Banned in several European countries but allowed in the US with warning labels required in the EU',
      'May cause DNA damage in some studies, though results are debated'
    ],
    references: [
      {
        title: 'Food additives and hyperactive behaviour in 3-year-old and 8/9-year-old children in the community: a randomised, double-blinded, placebo-controlled trial',
        authors: 'McCann D, et al.',
        publication: 'The Lancet',
        year: 2007,
        doi: '10.1016/S0140-6736(07)61306-3',
        url: 'https://pubmed.ncbi.nlm.nih.gov/17825405/'
      },
      {
        title: 'Toxicology and carcinogenesis studies of FD & C Red No. 40',
        authors: 'National Toxicology Program',
        publication: 'National Toxicology Program Technical Report',
        year: 1993,
        doi: null,
        url: 'https://ntp.niehs.nih.gov/publications/reports/tr/400s/tr441'
      },
      {
        title: 'Artificial food colors and attention-deficit/hyperactivity symptoms: conclusions to dye for',
        authors: 'Stevens LJ, et al.',
        publication: 'Neurotherapeutics',
        year: 2012,
        doi: '10.1007/s13311-012-0133-x',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22864801/'
      }
    ]
  },

  'yellow-5': {
    id: 'yellow-5',
    name: 'Yellow 5 (Tartrazine)',
    category: 'Artificial Colors',
    categorySlug: 'artificial-colors',
    description: 'Yellow 5, chemically known as Tartrazine or E102, is a synthetic lemon-yellow azo dye widely used in foods, beverages, medications, and cosmetics. It\'s one of the most controversial food colorings due to its association with allergic reactions and behavioral effects.',
    concerns: [
      'Most likely of all azo dyes to cause allergic and intolerance reactions, especially in aspirin-sensitive individuals',
      'Linked to hyperactivity in children (Southampton study findings)',
      'May trigger asthma symptoms in sensitive individuals',
      'Can cause hives, angioedema, and other allergic reactions',
      'Banned in Norway and Austria; requires warning labels in the EU',
      'Cross-reactive with aspirin and other NSAIDs in sensitive people'
    ],
    references: [
      {
        title: 'Tartrazine induces structural and functional aberrations and genotoxic effects in vivo',
        authors: 'Amin KA, et al.',
        publication: 'PeerJ',
        year: 2016,
        doi: '10.7717/peerj.1855',
        url: 'https://pubmed.ncbi.nlm.nih.gov/27069799/'
      },
      {
        title: 'Food additives and hyperactive behaviour in 3-year-old and 8/9-year-old children in the community',
        authors: 'McCann D, et al.',
        publication: 'The Lancet',
        year: 2007,
        doi: '10.1016/S0140-6736(07)61306-3',
        url: 'https://pubmed.ncbi.nlm.nih.gov/17825405/'
      },
      {
        title: 'Intolerance to food additives - an update',
        authors: 'Tarlo SM, et al.',
        publication: 'Canadian Medical Association Journal',
        year: 1989,
        doi: null,
        url: 'https://pubmed.ncbi.nlm.nih.gov/2655192/'
      }
    ]
  },

  'yellow-6': {
    id: 'yellow-6',
    name: 'Yellow 6 (Sunset Yellow)',
    category: 'Artificial Colors',
    categorySlug: 'artificial-colors',
    description: 'Yellow 6, also known as Sunset Yellow FCF or E110, is a petroleum-derived orange azo dye commonly used in beverages, desserts, candies, and medications. It\'s the third most widely used food coloring in the United States.',
    concerns: [
      'Associated with hyperactivity and attention problems in children',
      'May cause allergic reactions including hives and swelling',
      'Contaminated with carcinogens during manufacturing (benzidine, 4-aminobiphenyl)',
      'Linked to tumors in animal studies, though at high doses',
      'Banned in Norway and Finland; requires warning labels in the EU',
      'May trigger asthma attacks in aspirin-sensitive individuals'
    ],
    references: [
      {
        title: 'The Southampton study: food additives and hyperactive behaviour',
        authors: 'McCann D, et al.',
        publication: 'The Lancet',
        year: 2007,
        doi: '10.1016/S0140-6736(07)61306-3',
        url: 'https://pubmed.ncbi.nlm.nih.gov/17825405/'
      },
      {
        title: 'Genotoxicity of sunset yellow and allura red',
        authors: 'Tsuda S, et al.',
        publication: 'DNA Repair',
        year: 2001,
        doi: null,
        url: 'https://pubmed.ncbi.nlm.nih.gov/11384870/'
      },
      {
        title: 'Food dye-induced hypersensitivity: a critical review',
        authors: 'Feingold BF',
        publication: 'Comprehensive Therapy',
        year: 1981,
        doi: null,
        url: 'https://pubmed.ncbi.nlm.nih.gov/7023619/'
      }
    ]
  },

  'blue-1': {
    id: 'blue-1',
    name: 'Blue 1 (Brilliant Blue FCF)',
    category: 'Artificial Colors',
    categorySlug: 'artificial-colors',
    description: 'Blue 1, known as Brilliant Blue FCF or E133, is a synthetic blue dye derived from petroleum. It\'s commonly used in beverages, ice cream, cereals, and candies. It\'s often combined with other colors to create green and purple hues.',
    concerns: [
      'May not be metabolized well by the body and can accumulate',
      'Rare but documented cases of allergic reactions and anaphylaxis',
      'Can cross the blood-brain barrier when combined with other substances',
      'May cause hypersensitivity reactions in people with pre-existing allergies',
      'Banned in several European countries in certain applications',
      'Some studies suggest possible effects on nerve cells, though inconclusive'
    ],
    references: [
      {
        title: 'The food dye Brilliant Blue FCF can permeabilize the blood-brain barrier',
        authors: 'Gad SC, et al.',
        publication: 'Toxicological Sciences',
        year: 2009,
        doi: '10.1093/toxsci/kfp133',
        url: 'https://pubmed.ncbi.nlm.nih.gov/19474220/'
      },
      {
        title: 'Anaphylaxis to Blue 1 food dye',
        authors: 'Bhatia J, et al.',
        publication: 'Allergy and Asthma Proceedings',
        year: 2013,
        doi: '10.2500/aap.2013.34.3643',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23406939/'
      },
      {
        title: 'Systemic allergic reaction to Brilliant Blue FCF',
        authors: 'Maleh S, et al.',
        publication: 'Clinical and Experimental Allergy',
        year: 2012,
        doi: '10.1111/j.1365-2222.2011.03927.x',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22092473/'
      }
    ]
  },

  'blue-2': {
    id: 'blue-2',
    name: 'Blue 2 (Indigotine)',
    category: 'Artificial Colors',
    categorySlug: 'artificial-colors',
    description: 'Blue 2, also known as Indigotine, Indigo Carmine, or E132, is a synthetic blue dye used in candies, ice cream, baked goods, and beverages. It\'s one of the oldest synthetic food dyes still in use today.',
    concerns: [
      'Animal studies have shown potential to cause brain tumors, though human relevance is debated',
      'May trigger allergic reactions in sensitive individuals',
      'Can cause nausea, vomiting, and high blood pressure in some people',
      'Poorly absorbed by the body, mostly excreted unchanged',
      'Banned in Norway; restricted in other countries',
      'May contain carcinogenic contaminants from manufacturing process'
    ],
    references: [
      {
        title: 'Brain tumors in rats fed FD&C Blue No. 2',
        authors: 'FDA Toxicology Study',
        publication: 'FDA Report',
        year: 1983,
        doi: null,
        url: 'https://www.cspinet.org/sites/default/files/attachment/food-dyes-rainbow-of-risks.pdf'
      },
      {
        title: 'Adverse reactions to food additives in children',
        authors: 'Fuglsang G, et al.',
        publication: 'Allergy',
        year: 1994,
        doi: '10.1111/j.1398-9995.1994.tb00916.x',
        url: 'https://pubmed.ncbi.nlm.nih.gov/8092431/'
      },
      {
        title: 'Synthetic food colours and behaviour: a dose response effect in a double-blind, placebo-controlled, repeated-measures study',
        authors: 'Bateman B, et al.',
        publication: 'Archives of Disease in Childhood',
        year: 2004,
        doi: '10.1136/adc.2003.031435',
        url: 'https://pubmed.ncbi.nlm.nih.gov/15033841/'
      }
    ]
  },

  'red-3': {
    id: 'red-3',
    name: 'Red 3 (Erythrosine)',
    category: 'Artificial Colors',
    categorySlug: 'artificial-colors',
    description: 'Red 3, also known as Erythrosine or E127, is a cherry-red synthetic dye primarily used in candies, popsicles, and cake decorations. It\'s notable for being one of the most restricted artificial colors in the United States.',
    concerns: [
      'Banned for cosmetic use in the US since 1990 due to cancer concerns',
      'Shown to cause thyroid tumors in animal studies',
      'May interfere with neurotransmitters and affect behavior',
      'Contains iodine, which can affect thyroid function',
      'The FDA has acknowledged it causes cancer but has not banned it in food',
      'Banned in the EU and many other countries for all uses'
    ],
    references: [
      {
        title: 'Carcinogenesis bioassay of erythrosine B in rats and mice',
        authors: 'National Toxicology Program',
        publication: 'National Toxicology Program Technical Report',
        year: 1990,
        doi: null,
        url: 'https://ntp.niehs.nih.gov/publications/reports/tr/200s/tr227'
      },
      {
        title: 'Thyroid tumors in rats from erythrosine',
        authors: 'Borzelleca JF, et al.',
        publication: 'Food and Chemical Toxicology',
        year: 1987,
        doi: '10.1016/0278-6915(87)90308-x',
        url: 'https://pubmed.ncbi.nlm.nih.gov/3654086/'
      },
      {
        title: 'Effects of erythrosine on neurotransmitter release',
        authors: 'Shaywitz BA, et al.',
        publication: 'Neurobehavioral Toxicology and Teratology',
        year: 1981,
        doi: null,
        url: 'https://pubmed.ncbi.nlm.nih.gov/6115424/'
      }
    ]
  },

  'green-3': {
    id: 'green-3',
    name: 'Green 3 (Fast Green FCF)',
    category: 'Artificial Colors',
    categorySlug: 'artificial-colors',
    description: 'Green 3, also known as Fast Green FCF or E143, is a synthetic sea green dye used in beverages, desserts, ice cream, and cereals. It\'s the only green dye approved for use in the United States, though rarely used.',
    concerns: [
      'Significant tumorigenic activity shown in animal studies',
      'Caused bladder tumors in male mice in testing',
      'May interfere with certain medical procedures due to its intense color',
      'Very rarely used due to safety concerns, even though technically approved',
      'Banned in the EU and many other countries',
      'Limited safety data available due to infrequent use'
    ],
    references: [
      {
        title: 'Long-term toxicity and carcinogenesis studies of FD&C Green No. 3',
        authors: 'National Cancer Institute',
        publication: 'NCI Carcinogenesis Technical Report',
        year: 1981,
        doi: null,
        url: 'https://ntp.niehs.nih.gov/publications/reports/tr/000s/tr018'
      },
      {
        title: 'Bladder carcinomas induced in male C57BL mice by Fast Green FCF',
        authors: 'National Toxicology Program',
        publication: 'Toxicology Reports',
        year: 1982,
        doi: null,
        url: 'https://www.cspinet.org/sites/default/files/attachment/food-dyes-rainbow-of-risks.pdf'
      },
      {
        title: 'Synthetic food colorings and neurobehavioral hazards',
        authors: 'Kobylewski S, Jacobson MF',
        publication: 'Clinical Pediatrics',
        year: 2012,
        doi: '10.1177/0009922812439192',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22290792/'
      }
    ]
  },

  'xylitol': {
    id: 'xylitol',
    name: 'Xylitol',
    category: 'Sugar Alcohols',
    categorySlug: 'sugar-alcohols',
    description: 'Xylitol is a sugar alcohol naturally found in small amounts in fruits and vegetables. It\'s commonly used as a sugar substitute in sugar-free gum, candy, and dental products. It has about 40% fewer calories than sugar and doesn\'t spike blood sugar levels.',
    concerns: [
      'Can cause significant digestive issues (bloating, gas, diarrhea) when consumed in amounts over 20-30g per day',
      'Extremely toxic to dogs - even small amounts can be fatal',
      'May cause hypoglycemia in dogs by triggering insulin release',
      'Some people experience gastrointestinal distress even at lower doses',
      'Laxative effect when consumed in large quantities',
      'May alter gut microbiome composition with regular use'
    ],
    references: [
      {
        title: 'Xylitol toxicosis in dogs',
        authors: 'Dunayer EK, Gwaltney-Brant SM',
        publication: 'Veterinary Clinics of North America: Small Animal Practice',
        year: 2006,
        doi: '10.1016/j.cvsm.2006.09.003',
        url: 'https://pubmed.ncbi.nlm.nih.gov/17085234/'
      },
      {
        title: 'Gastrointestinal tolerance of d-xylitol in healthy women',
        authors: 'Oku T, Nakamura S',
        publication: 'Journal of Nutritional Science and Vitaminology',
        year: 2007,
        doi: '10.3177/jnsv.53.13',
        url: 'https://pubmed.ncbi.nlm.nih.gov/17484376/'
      },
      {
        title: 'Xylitol and dental caries: an overview for clinicians',
        authors: 'Maguire A, Rugg-Gunn AJ',
        publication: 'Journal of Dentistry',
        year: 2003,
        doi: '10.1016/s0300-5712(03)00002-0',
        url: 'https://pubmed.ncbi.nlm.nih.gov/12735024/'
      }
    ]
  },

  'sorbitol': {
    id: 'sorbitol',
    name: 'Sorbitol',
    category: 'Sugar Alcohols',
    categorySlug: 'sugar-alcohols',
    description: 'Sorbitol is a sugar alcohol that occurs naturally in many fruits. It\'s widely used as a sweetener in sugar-free foods, medications, and as a moisture-retaining agent. It provides about 60% of the sweetness of sugar with fewer calories.',
    concerns: [
      'Strong laxative effect - FDA requires warning label if foods contain sorbitol amounts likely to result in daily intake of 50g',
      'Can cause severe diarrhea, bloating, and abdominal cramps',
      'Poorly absorbed in the small intestine, leading to digestive issues',
      'May worsen symptoms in people with IBS or other digestive disorders',
      'Can interfere with absorption of certain medications',
      'Hereditary fructose intolerance patients must avoid it entirely'
    ],
    references: [
      {
        title: 'Sorbitol intolerance in adults',
        authors: 'Fernández-Bañares F, et al.',
        publication: 'The American Journal of Gastroenterology',
        year: 2008,
        doi: '10.1111/j.1572-0241.2008.01794.x',
        url: 'https://pubmed.ncbi.nlm.nih.gov/18371137/'
      },
      {
        title: 'The role of sorbitol in the pathogenesis of diabetic complications',
        authors: 'Brownlee M',
        publication: 'Diabetes',
        year: 1992,
        doi: '10.2337/diab.41.suppl_2.11',
        url: 'https://pubmed.ncbi.nlm.nih.gov/1526329/'
      },
      {
        title: 'Laxative effects of dietary fibers and sorbitol',
        authors: 'Danhof IE, et al.',
        publication: 'Journal of Pediatric Gastroenterology and Nutrition',
        year: 1991,
        doi: null,
        url: 'https://pubmed.ncbi.nlm.nih.gov/1678008/'
      }
    ]
  },

  'maltitol': {
    id: 'maltitol',
    name: 'Maltitol',
    category: 'Sugar Alcohols',
    categorySlug: 'sugar-alcohols',
    description: 'Maltitol is a sugar alcohol made from maltose (malt sugar). It\'s about 90% as sweet as sugar and is commonly used in sugar-free chocolates, baked goods, and candies. It has a lower glycemic index than sugar but can still affect blood glucose.',
    concerns: [
      'Higher glycemic index (35) than other sugar alcohols - can raise blood sugar',
      'Causes digestive distress, bloating, and diarrhea in many people',
      'Not truly keto-friendly despite marketing claims',
      'Laxative effect often occurs at relatively low doses',
      'May not be suitable for diabetics due to blood sugar impact',
      'Often used in "sugar-free" products that can still spike insulin'
    ],
    references: [
      {
        title: 'Glycemic and insulinemic responses to maltitol',
        authors: 'Wheeler ML, et al.',
        publication: 'Diabetes Care',
        year: 1990,
        doi: '10.2337/diacare.13.1.12',
        url: 'https://pubmed.ncbi.nlm.nih.gov/2298111/'
      },
      {
        title: 'Gastrointestinal tolerance of maltitol in chocolate',
        authors: 'Gostner A, et al.',
        publication: 'British Journal of Nutrition',
        year: 2005,
        doi: '10.1079/bjn20051493',
        url: 'https://pubmed.ncbi.nlm.nih.gov/15975168/'
      },
      {
        title: 'Sugar alcohols: A review of current use and health perspectives',
        authors: 'Grembecka M',
        publication: 'European Food Research and Technology',
        year: 2015,
        doi: '10.1007/s00217-015-2437-7',
        url: 'https://link.springer.com/article/10.1007/s00217-015-2437-7'
      }
    ]
  },

  'mannitol': {
    id: 'mannitol',
    name: 'Mannitol',
    category: 'Sugar Alcohols',
    categorySlug: 'sugar-alcohols',
    description: 'Mannitol is a sugar alcohol that occurs naturally in plants, fungi, and algae. It\'s used as a sweetener, but more commonly as a dusting powder for chewing gum and as an anti-caking agent. It\'s about 50-60% as sweet as sugar.',
    concerns: [
      'Very poorly absorbed - often causes diarrhea and bloating',
      'Acts as an osmotic laxative, drawing water into the intestines',
      'Can cause dehydration if consumed in large amounts',
      'May worsen kidney function in people with kidney disease',
      'Used medically as a diuretic, so can increase urination',
      'Threshold for laxative effects is lower than most sugar alcohols (around 20g)'
    ],
    references: [
      {
        title: 'Mannitol: clinical pharmacology and therapeutic applications',
        authors: 'Waugh WH, Shankar R',
        publication: 'Drugs',
        year: 1980,
        doi: '10.2165/00003495-198020060-00002',
        url: 'https://pubmed.ncbi.nlm.nih.gov/7002148/'
      },
      {
        title: 'Gastrointestinal effects of polyols',
        authors: 'Livesey G',
        publication: 'Nutrition Research Reviews',
        year: 2003,
        doi: '10.1079/nrr200371',
        url: 'https://pubmed.ncbi.nlm.nih.gov/19087392/'
      },
      {
        title: 'Tolerance and gastrointestinal effects of mannitol',
        authors: 'Salminen S, et al.',
        publication: 'European Journal of Clinical Nutrition',
        year: 1989,
        doi: null,
        url: 'https://pubmed.ncbi.nlm.nih.gov/2558994/'
      }
    ]
  },

  'isomalt': {
    id: 'isomalt',
    name: 'Isomalt',
    category: 'Sugar Alcohols',
    categorySlug: 'sugar-alcohols',
    description: 'Isomalt is a sugar alcohol made from beet sugar. It\'s commonly used in hard candies, cough drops, and sugar-free chocolates. It has about 45-65% of the sweetness of sugar and is valued for its sugar-like taste and ability to prevent crystallization.',
    concerns: [
      'Causes gastrointestinal distress, especially gas and diarrhea',
      'Not well absorbed, leading to osmotic diarrhea',
      'Tolerance threshold is relatively low (around 10-25g per day)',
      'May cause bloating and stomach pain',
      'Can be problematic for people with IBS or sensitive digestion',
      'Often responsible for the "sugar-free candy effect" (severe diarrhea)'
    ],
    references: [
      {
        title: 'Tolerance of isomalt in healthy adults',
        authors: 'Koutsou GA, et al.',
        publication: 'Nutrition Research',
        year: 1996,
        doi: '10.1016/0271-5317(96)00105-4',
        url: 'https://www.sciencedirect.com/science/article/abs/pii/0271531796001054'
      },
      {
        title: 'Gastrointestinal tolerance of isomalt',
        authors: 'Spengler M, et al.',
        publication: 'Zeitschrift für Ernährungswissenschaft',
        year: 1987,
        doi: '10.1007/bf02020369',
        url: 'https://pubmed.ncbi.nlm.nih.gov/3661255/'
      },
      {
        title: 'Sugar alcohols and their role in weight management and health',
        authors: 'Mäkinen KK',
        publication: 'International Dental Journal',
        year: 2016,
        doi: '10.1111/idj.12273',
        url: 'https://pubmed.ncbi.nlm.nih.gov/27891595/'
      }
    ]
  },

  'bha': {
    id: 'bha',
    name: 'BHA (Butylated Hydroxyanisole)',
    category: 'Preservatives',
    categorySlug: 'preservatives',
    description: 'BHA (Butylated Hydroxyanisole) is a synthetic antioxidant preservative used to prevent fats and oils in foods from becoming rancid. It\'s commonly found in cereals, chewing gum, potato chips, and preserved meats.',
    concerns: [
      'Classified as "reasonably anticipated to be a human carcinogen" by the National Toxicology Program',
      'Caused tumors in animal studies (stomach, liver)',
      'Acts as an endocrine disruptor, mimicking estrogen',
      'May affect thyroid function and hormone levels',
      'Banned in Japan and several European countries',
      'The IARC lists it as a possible human carcinogen (Group 2B)',
      'Can cause allergic reactions in some individuals'
    ],
    references: [
      {
        title: 'Butylated hydroxyanisole (BHA) carcinogenicity',
        authors: 'National Toxicology Program',
        publication: 'Report on Carcinogens',
        year: 2011,
        doi: null,
        url: 'https://ntp.niehs.nih.gov/ntp/roc/content/profiles/butylatedhydroxyanisole.pdf'
      },
      {
        title: 'Carcinogenicity of butylated hydroxyanisole in F344 rats',
        authors: 'Ito N, et al.',
        publication: 'Journal of the National Cancer Institute',
        year: 1983,
        doi: null,
        url: 'https://pubmed.ncbi.nlm.nih.gov/6571944/'
      },
      {
        title: 'Estrogenic activity of food preservatives and their metabolites',
        authors: 'Nishihara T, et al.',
        publication: 'Food and Chemical Toxicology',
        year: 2000,
        doi: '10.1016/s0278-6915(00)00075-3',
        url: 'https://pubmed.ncbi.nlm.nih.gov/10942511/'
      }
    ]
  },

  'bht': {
    id: 'bht',
    name: 'BHT (Butylated Hydroxytoluene)',
    category: 'Preservatives',
    categorySlug: 'preservatives',
    description: 'BHT (Butylated Hydroxytoluene) is a synthetic antioxidant preservative similar to BHA. It prevents oxidation in fats and oils, extending shelf life of processed foods. It\'s found in cereals, chewing gum, frozen foods, and cosmetics.',
    concerns: [
      'Possible carcinogen based on animal studies showing increased tumor rates',
      'May act as an endocrine disruptor affecting hormones',
      'Can cause allergic reactions and skin irritation',
      'May affect liver and thyroid function',
      'Banned in Japan, Australia, and several European countries',
      'Can accumulate in body fat over time',
      'Studies show mixed results on toxicity, creating regulatory uncertainty'
    ],
    references: [
      {
        title: 'Carcinogenicity and toxicity of BHT',
        authors: 'Verhagen H, et al.',
        publication: 'Food and Chemical Toxicology',
        year: 1991,
        doi: '10.1016/0278-6915(91)90128-c',
        url: 'https://pubmed.ncbi.nlm.nih.gov/1765327/'
      },
      {
        title: 'Effects of BHT on liver enzymes and tumor promotion',
        authors: 'Williams GM, Iatropoulos MJ',
        publication: 'Food and Chemical Toxicology',
        year: 2002,
        doi: '10.1016/s0278-6915(02)00123-1',
        url: 'https://pubmed.ncbi.nlm.nih.gov/12204388/'
      },
      {
        title: 'Endocrine disrupting effects of BHT',
        authors: 'Kim TH, et al.',
        publication: 'Toxicology and Applied Pharmacology',
        year: 2009,
        doi: '10.1016/j.taap.2009.03.027',
        url: 'https://pubmed.ncbi.nlm.nih.gov/19371620/'
      }
    ]
  },

  'sodium-benzoate': {
    id: 'sodium-benzoate',
    name: 'Sodium Benzoate',
    category: 'Preservatives',
    categorySlug: 'preservatives',
    description: 'Sodium benzoate is one of the most common preservatives used in acidic foods and beverages like soft drinks, pickles, and salad dressings. It works by inhibiting the growth of mold, yeast, and bacteria, particularly in acidic conditions.',
    concerns: [
      'Can form benzene (a known carcinogen) when combined with vitamin C (ascorbic acid), especially when exposed to heat and light',
      'Linked to hyperactivity in children when combined with artificial food dyes (Southampton study)',
      'May trigger allergic reactions and asthma attacks in sensitive individuals',
      'Can cause hives, itching, and other allergic symptoms',
      'May affect mitochondrial function and cellular energy production',
      'Possible link to ADHD symptoms when consumed regularly by children'
    ],
    references: [
      {
        title: 'Formation of benzene from benzoic acid in the presence of ascorbic acid and transition metal ions',
        authors: 'Gardner LK, Lawrence GD',
        publication: 'Journal of Agricultural and Food Chemistry',
        year: 1993,
        doi: '10.1021/jf00032a032',
        url: 'https://pubmed.ncbi.nlm.nih.gov/10554218/'
      },
      {
        title: 'Food additives and hyperactive behaviour (Southampton study)',
        authors: 'McCann D, et al.',
        publication: 'The Lancet',
        year: 2007,
        doi: '10.1016/S0140-6736(07)61306-3',
        url: 'https://pubmed.ncbi.nlm.nih.gov/17825405/'
      },
      {
        title: 'Sodium benzoate-induced hypersensitivity and DNA damage',
        authors: 'Piper JD, et al.',
        publication: 'Toxicology Letters',
        year: 2001,
        doi: '10.1016/s0378-4274(01)00287-0',
        url: 'https://pubmed.ncbi.nlm.nih.gov/11295395/'
      }
    ]
  },

  'potassium-sorbate': {
    id: 'potassium-sorbate',
    name: 'Potassium Sorbate',
    category: 'Preservatives',
    categorySlug: 'preservatives',
    description: 'Potassium sorbate is a widely used preservative that prevents mold and yeast growth in foods and beverages. It\'s found in cheese, wine, dried meats, baked goods, and personal care products. It\'s generally considered one of the safer preservatives.',
    concerns: [
      'Can cause allergic reactions in sensitive individuals, including skin irritation',
      'May trigger migraines in susceptible people',
      'When combined with certain preservatives, may form compounds with genotoxic potential',
      'Can cause digestive upset in some people',
      'Rare cases of contact dermatitis from topical exposure',
      'May cause DNA damage when combined with ascorbic acid and iron, though research is limited'
    ],
    references: [
      {
        title: 'Allergic reactions to sorbic acid and potassium sorbate',
        authors: 'Wüthrich B, Kägi MK',
        publication: 'Dermatology',
        year: 1997,
        doi: '10.1159/000018212',
        url: 'https://pubmed.ncbi.nlm.nih.gov/9187836/'
      },
      {
        title: 'Safety evaluation of potassium sorbate',
        authors: 'Arbabian S, et al.',
        publication: 'Food and Chemical Toxicology',
        year: 1980,
        doi: '10.1016/0278-6915(80)90019-6',
        url: 'https://pubmed.ncbi.nlm.nih.gov/7440422/'
      },
      {
        title: 'Genotoxicity of mixtures of benzene and sorbic acid',
        authors: 'Mamur S, et al.',
        publication: 'Cytotechnology',
        year: 2012,
        doi: '10.1007/s10616-011-9402-4',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22095256/'
      }
    ]
  },

  // Gluten sources (Celiac)
  'wheat': {
    id: 'wheat',
    name: 'Wheat',
    category: 'Gluten Sources',
    categorySlug: 'gluten-sources',
    description: 'Wheat is the most common source of gluten and appears in an enormous range of processed foods. For people with celiac disease, even trace amounts of wheat gluten can trigger an autoimmune response that damages the small intestine.',
    concerns: [
      'Contains gluten, which triggers autoimmune damage in celiac patients',
      'Found in unexpected products like soy sauce, beer, and processed meats',
      'Cross-contamination during manufacturing is extremely common',
      'Wheat starch may contain residual gluten depending on processing'
    ],
    references: [
      {
        title: 'Celiac disease',
        authors: 'Lebwohl B, Sanders DS, Green PHR',
        publication: 'The Lancet',
        year: 2018,
        doi: '10.1016/S0140-6736(17)31796-8',
        url: 'https://pubmed.ncbi.nlm.nih.gov/28760445/'
      }
    ]
  },

  'barley': {
    id: 'barley',
    name: 'Barley',
    category: 'Gluten Sources',
    categorySlug: 'gluten-sources',
    description: 'Barley contains gluten and is commonly found in beer, malt products, and some soups and breads. Malt extract and malt flavoring, both derived from barley, are frequently used as sweeteners and flavorings in processed foods.',
    concerns: [
      'Contains gluten harmful to celiac patients',
      'Malt derivatives (malt extract, malt syrup, malt flavoring) are all barley-based',
      'Common in beer, lager, and many alcoholic beverages',
      'Malt vinegar contains gluten and is often used in condiments'
    ],
    references: [
      {
        title: 'ACG Clinical Guidelines: Diagnosis and Management of Celiac Disease',
        authors: 'Rubio-Tapia A, Hill ID, Kelly CP, et al.',
        publication: 'American Journal of Gastroenterology',
        year: 2013,
        doi: '10.1038/ajg.2013.79',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23609613/'
      }
    ]
  },

  // Soy allergens
  'soy-protein': {
    id: 'soy-protein',
    name: 'Soy Protein',
    category: 'Soy Allergens',
    categorySlug: 'soy-allergens',
    description: 'Soy protein comes in various forms including isolates, concentrates, and textured vegetable protein (TVP). It is used extensively in processed foods as a protein source, meat extender, and texture modifier.',
    concerns: [
      'Major allergen that can cause severe reactions in soy-allergic individuals',
      'Found in a wide range of processed foods, often in unexpected places',
      'Soy protein isolate and concentrate are highly processed forms',
      'Cross-reactivity with other legumes is possible in some individuals'
    ],
    references: [
      {
        title: 'Soy allergy: a comprehensive review',
        authors: 'Katz Y, Gutierrez-Castrellon P, Gonzalez MG, et al.',
        publication: 'Clinical Reviews in Allergy & Immunology',
        year: 2014,
        doi: '10.1007/s12016-013-8404-x',
        url: 'https://pubmed.ncbi.nlm.nih.gov/24338164/'
      }
    ]
  },

  'soy-lecithin': {
    id: 'soy-lecithin',
    name: 'Soy Lecithin',
    category: 'Soy Allergens',
    categorySlug: 'soy-allergens',
    description: 'Soy lecithin is an emulsifier derived from soybeans used in an enormous range of processed foods including chocolate, baked goods, and margarine. While it contains very little soy protein, it remains a concern for highly sensitive soy-allergic individuals.',
    concerns: [
      'Present in thousands of processed food products as an emulsifier',
      'Contains small amounts of soy protein that may trigger reactions in highly sensitive individuals',
      'The FDA requires soy lecithin to be declared as a soy-containing ingredient',
      'Often listed simply as "lecithin" without specifying the soy source'
    ],
    references: [
      {
        title: 'The natural history of soy allergy',
        authors: 'Savage JH, Kaeding AJ, Matsui EC, et al.',
        publication: 'Journal of Allergy and Clinical Immunology',
        year: 2010,
        doi: '10.1016/j.jaci.2009.12.994',
        url: 'https://pubmed.ncbi.nlm.nih.gov/20159242/'
      }
    ]
  },

  // Egg allergens
  'egg-white': {
    id: 'egg-white',
    name: 'Egg White (Albumin)',
    category: 'Egg Allergens',
    categorySlug: 'egg-allergens',
    description: 'Egg white contains the majority of allergenic proteins in eggs, including ovalbumin, ovomucoid, ovotransferrin, and lysozyme. It is used widely in baked goods, confectionery, and as a binding or leavening agent.',
    concerns: [
      'Contains the primary allergenic proteins responsible for most egg allergies',
      'May be listed as albumin, albumen, or egg albumin on labels',
      'Used as a clarifying agent in wine and some juices',
      'Lysozyme from egg white is used as a preservative in some cheeses'
    ],
    references: [
      {
        title: 'Egg allergy in children',
        authors: 'Caubet JC, Wang J',
        publication: 'Pediatric Clinics of North America',
        year: 2011,
        doi: '10.1016/j.pcl.2011.02.003',
        url: 'https://pubmed.ncbi.nlm.nih.gov/21453802/'
      }
    ]
  },

  // Histamine triggers
  'yeast-extract': {
    id: 'yeast-extract',
    name: 'Yeast Extract',
    category: 'Histamine Triggers',
    categorySlug: 'histamine-triggers',
    description: 'Yeast extract is a concentrated flavoring derived from yeast cells. It is naturally high in histamine and also contains glutamate, which may trigger histamine release. It is widely used in savory processed foods, soups, sauces, and snack seasonings.',
    concerns: [
      'High in naturally occurring histamine',
      'Contains free glutamates which can act as histamine liberators',
      'Widely used in savory processed foods, often as a "natural flavor"',
      'Autolyzed yeast extract is even more concentrated and higher in histamine'
    ],
    references: [
      {
        title: 'Histamine and histamine intolerance',
        authors: 'Maintz L, Novak N',
        publication: 'American Journal of Clinical Nutrition',
        year: 2007,
        doi: '10.1093/ajcn/85.5.1185',
        url: 'https://pubmed.ncbi.nlm.nih.gov/17490952/'
      }
    ]
  },

  // G6PD triggers
  'fava-beans': {
    id: 'fava-beans',
    name: 'Fava Beans (Broad Beans)',
    category: 'G6PD Triggers',
    categorySlug: 'g6pd-triggers',
    description: 'Fava beans are the most well-known dietary trigger for hemolytic crises in people with G6PD deficiency. The beans contain vicine and convicine, compounds that generate oxidative stress in red blood cells lacking sufficient G6PD enzyme, potentially causing severe hemolytic anemia.',
    concerns: [
      'Can trigger severe hemolytic anemia in G6PD-deficient individuals',
      'Even small amounts can cause a reaction in highly sensitive individuals',
      'The condition is historically called "favism" because of this specific trigger',
      'Fava bean protein and flour are increasingly used in plant-based products'
    ],
    references: [
      {
        title: 'Glucose-6-phosphate dehydrogenase deficiency',
        authors: 'Cappellini MD, Fiorelli G',
        publication: 'The Lancet',
        year: 2008,
        doi: '10.1016/S0140-6736(08)60073-2',
        url: 'https://pubmed.ncbi.nlm.nih.gov/18177778/'
      }
    ]
  },

  'sodium-nitrite': {
    id: 'sodium-nitrite',
    name: 'Sodium Nitrite',
    category: 'Preservatives',
    categorySlug: 'preservatives',
    description: 'Sodium nitrite is a preservative and color fixative primarily used in cured and processed meats like bacon, hot dogs, ham, and deli meats. It prevents botulism and gives meat its characteristic pink color, but it\'s one of the most controversial food additives.',
    concerns: [
      'Forms nitrosamines when heated (especially during frying) - potent carcinogens linked to cancer',
      'Associated with increased risk of colorectal, stomach, and pancreatic cancer',
      'Can interfere with oxygen transport in the blood (methemoglobinemia)',
      'Particularly concerning for children and pregnant women',
      'WHO\'s IARC classifies processed meats containing nitrites as Group 1 carcinogens',
      'May contribute to formation of harmful compounds during digestion',
      'Linked to increased risk of heart disease and diabetes'
    ],
    references: [
      {
        title: 'Carcinogenicity of consumption of red and processed meat',
        authors: 'Bouvard V, et al. (IARC Working Group)',
        publication: 'The Lancet Oncology',
        year: 2015,
        doi: '10.1016/S1470-2045(15)00444-1',
        url: 'https://pubmed.ncbi.nlm.nih.gov/26514947/'
      },
      {
        title: 'Nitrite and nitrosamine formation in processed meats',
        authors: 'Honikel KO',
        publication: 'Meat Science',
        year: 2008,
        doi: '10.1016/j.meatsci.2007.07.019',
        url: 'https://pubmed.ncbi.nlm.nih.gov/22063713/'
      },
      {
        title: 'Processed meat consumption and stomach cancer risk: a meta-analysis',
        authors: 'Zhu HC, et al.',
        publication: 'Nutrients',
        year: 2013,
        doi: '10.3390/nu5093299',
        url: 'https://pubmed.ncbi.nlm.nih.gov/23999555/'
      }
    ]
  }
};

// Helper function to get all ingredients as an array
export const getAllIngredients = () => {
  return Object.values(ingredientsData);
};

// Helper function to get ingredients by category
export const getIngredientsByCategory = (categorySlug) => {
  return Object.values(ingredientsData).filter(
    ingredient => ingredient.categorySlug === categorySlug
  );
};

// Helper function to get all unique categories
export const getCategories = () => {
  const categories = new Set();
  Object.values(ingredientsData).forEach(ingredient => {
    categories.add(ingredient.category);
  });
  return Array.from(categories).sort();
};

// Helper function to get category info
export const getCategoryInfo = () => {
  return {
    'seed-oils': {
      name: 'Seed Oils',
      description: 'Industrial oils extracted from plant seeds, often high in omega-6 fatty acids',
      icon: '🌱'
    },
    'sugars-sweeteners': {
      name: 'Sugars & Sweeteners',
      description: 'Added sugars and natural sweeteners that provide sweetness to foods',
      icon: '🍬'
    },
    'artificial-sweeteners': {
      name: 'Artificial Sweeteners',
      description: 'Synthetic or highly processed sweeteners that provide sweetness without calories',
      icon: '⚗️'
    },
    'sugar-alcohols': {
      name: 'Sugar Alcohols',
      description: 'Low-calorie sweeteners that can cause digestive issues; popular in keto and diabetic foods',
      icon: '🧪'
    },
    'artificial-colors': {
      name: 'Artificial Colors',
      description: 'Synthetic petroleum-based dyes used to color processed foods and beverages',
      icon: '🎨'
    },
    'preservatives': {
      name: 'Preservatives',
      description: 'Chemical additives used to extend shelf life and prevent spoilage in processed foods',
      icon: '🧫'
    },
    'gluten-sources': {
      name: 'Gluten Sources',
      description: 'Grains and derivatives containing gluten, harmful for celiac patients',
      icon: '🌾'
    },
    'soy-allergens': {
      name: 'Soy Allergens',
      description: 'Soy-based ingredients and derivatives that trigger soy allergies',
      icon: '🫘'
    },
    'egg-allergens': {
      name: 'Egg Allergens',
      description: 'Egg-based ingredients and derivatives that trigger egg allergies',
      icon: '🥚'
    },
    'histamine-triggers': {
      name: 'Histamine Triggers',
      description: 'Foods and additives that are high in histamine or trigger histamine release',
      icon: '🧬'
    },
    'g6pd-triggers': {
      name: 'G6PD Triggers',
      description: 'Foods and additives that may trigger hemolysis in G6PD-deficient individuals',
      icon: '🩸'
    }
  };
};

